import React, { useState, useEffect } from 'react';
import { ArrowLeft, Bookmark, Grid3X3, Heart, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { getSavedPosts, getUserCollections } from '../lib/engagementService';

export const SavedPostsPage: React.FC = () => {
  const { user } = useAuthStore();
  const [savedPosts, setSavedPosts] = useState<any[]>([]);
  const [collections, setCollections] = useState<string[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<string>('All Posts');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadSavedPosts();
      loadCollections();
    }
  }, [user]);

  const loadSavedPosts = async () => {
    try {
      setLoading(true);
      const posts = await getSavedPosts(user?.id || '');
      setSavedPosts(posts);
    } catch (error) {
      console.error('Error loading saved posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCollections = async () => {
    try {
      const userCollections = await getUserCollections(user?.id || '');
      setCollections(['All Posts', ...userCollections.filter(c => c !== 'All Posts')]);
    } catch (error) {
      console.error('Error loading collections:', error);
    }
  };

  const filteredPosts = selectedCollection === 'All Posts' 
    ? savedPosts 
    : savedPosts.filter(saved => saved.collection_name === selectedCollection);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="pt-16 pb-16 md:pt-20 md:pb-0 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 max-w-screen-lg">
        {/* Header */}
        <div className="bg-white border border-gray-200 rounded-md mb-6 overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center">
              <Link to="/profile" className="text-gray-600 mr-4">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div className="flex items-center">
                <Bookmark className="h-5 w-5 mr-2" />
                <h1 className="text-lg font-medium">Saved Posts</h1>
              </div>
            </div>
            <div className="flex items-center">
              <Grid3X3 className="h-5 w-5 text-gray-500" />
            </div>
          </div>

          {/* Collections Filter */}
          {collections.length > 1 && (
            <div className="p-4 border-b border-gray-200">
              <div className="flex space-x-2 overflow-x-auto">
                {collections.map((collection) => (
                  <button
                    key={collection}
                    onClick={() => setSelectedCollection(collection)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                      selectedCollection === collection
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {collection}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Saved Posts Grid */}
        {filteredPosts.length > 0 ? (
          <div className="grid grid-cols-3 gap-1 md:gap-4">
            {filteredPosts.map((saved) => {
              const post = saved.posts;
              const firstMedia = post.post_media && post.post_media.length > 0 
                ? post.post_media[0] 
                : null;
              const imageUrl = firstMedia?.media_url;

              return (
                <div key={saved.id} className="relative pb-[100%] bg-gray-100 overflow-hidden group">
                  {imageUrl ? (
                    <>
                      <img 
                        src={imageUrl}
                        alt="Saved post"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => {
                          console.error('Saved post image failed to load:', imageUrl);
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      
                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="flex items-center space-x-4 text-white">
                          <div className="flex items-center">
                            <Heart className="h-5 w-5 mr-1 fill-white" />
                            <span className="font-semibold">
                              {post.post_likes?.length || 0}
                            </span>
                          </div>
                          <div className="flex items-center">
                            <MessageCircle className="h-5 w-5 mr-1 fill-white" />
                            <span className="font-semibold">
                              {post.post_comments?.length || 0}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Collection Badge */}
                      {saved.collection_name !== 'All Posts' && (
                        <div className="absolute top-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                          {saved.collection_name}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-gray-400 bg-gray-100">
                      <p className="text-sm">No media</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-md p-8 text-center">
            <div className="mb-4">
              <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                <Bookmark className="h-8 w-8 text-gray-400" />
              </div>
            </div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">No saved posts</h3>
            <p className="text-gray-500 mb-4">
              {selectedCollection === 'All Posts' 
                ? "Save posts you want to see again by tapping the bookmark icon."
                : `No posts saved in "${selectedCollection}" collection.`
              }
            </p>
            <Link 
              to="/" 
              className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
            >
              Explore posts
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};